/**
 * RENDER — SECTIONS (Hotels / Flights / OtherServices) + Service card UI.
 *
 * Phase 5 modularization (Sprint 6, 2026-05-07).
 * Извлечено из public/js/app.js (DOMAIN: RENDER — SECTIONS).
 *
 * Три отдельные секции в калькуляторе сметы для услуг, не привязанных
 * к основному списку «по дням»:
 *   - Отели    (state.hotels)        — с сортировкой по defaultDay (Phase 2)
 *   - Перелёты (state.flights)
 *   - Прочие   (state.otherServices) — то что без day
 *
 * Render:
 *   - renderHotels() / renderFlights() / renderOtherServices()
 *   - renderServiceCard(service, type) — большая универсальная карточка (~190 строк)
 *
 * Modal helpers:
 *   - showAddHotelModal / showAddFlightModal / showAddOtherServiceModal
 *
 * @module app/render-sections
 */

// i18n helper: returns translation if loaded, else fallback (EN). Safe to call before module loads.
const _t = (k, fb) => {
    const v = window.i18n?.t?.(k);
    return (v && v !== k) ? v : (fb || k);
};

/**
 * Прикрепляет render-методы для отелей/перелётов/прочих услуг.
 * @param {Function} ClassRef - класс ProfessionalQuoteCalculator
 */
export function attachRenderSections(ClassRef) {
    Object.assign(ClassRef.prototype, {
        renderHotels() {
            console.log('[RENDER] renderHotels() called');
            const container = document.getElementById('hotels-list');
            if (!container) return;

            if (this.state.hotels.length === 0) {
                container.innerHTML = `<div class="text-xs text-muted" style="padding: calc(var(--spacing) * 4); text-align: center;">${_t('app.render.hotels_empty', 'No hotels added')}</div>`;
                return;
            }

            // 🆕 Group 2: сортировка отелей по полю day (порядковый день тура).
            //   Отели без day (null/0) идут в конец списка. Стабильно: при равных day
            //   сохраняется исходный порядок добавления.
            const sorted = this.state.hotels
                .map((s, i) => ({ s, i }))
                .sort((a, b) => {
                    const da = a.s.day || Number.MAX_SAFE_INTEGER;
                    const db = b.s.day || Number.MAX_SAFE_INTEGER;
                    if (da !== db) return da - db;
                    return a.i - b.i;
                })
                .map(x => x.s);

            // Phase 8a: применяем quick-filter (по статусу бронирования/оплаты).
            const visible = sorted.filter(s => this.passesQuickFilter(s));

            container.innerHTML = visible.map(service => this.renderServiceCard(service, 'hotel')).join('');
            lucide.createIcons();
        },

        renderFlights() {
            console.log('[RENDER] renderFlights() called');
            const container = document.getElementById('flights-list');
            if (!container) return;

            if (this.state.flights.length === 0) {
                container.innerHTML = `<div class="text-xs text-muted" style="padding: calc(var(--spacing) * 4); text-align: center;">${_t('app.render.flights_empty', 'No flights added')}</div>`;
                return;
            }

            // Phase 8a: применяем quick-filter (по статусу бронирования/оплаты).
            const visible = this.state.flights.filter(s => this.passesQuickFilter(s));

            container.innerHTML = visible.map(service => this.renderServiceCard(service, 'flight')).join('');
            lucide.createIcons();
        },

        renderOtherServices() {
            console.log('[RENDER] renderOtherServices() called');
            const container = document.getElementById('other-services-list');
            if (!container) return;

            if (this.state.otherServices.length === 0) {
                container.innerHTML = `<div class="text-xs text-muted" style="padding: calc(var(--spacing) * 4); text-align: center;">${_t('app.render.other_empty', 'No other services added')}</div>`;
                return;
            }

            // Phase 8a: применяем quick-filter (по статусу бронирования/оплаты).
            const visible = this.state.otherServices.filter(s => this.passesQuickFilter(s));

            container.innerHTML = visible.map(service => this.renderServiceCard(service, 'other')).join('');
            lucide.createIcons();
        },

        renderServiceCard(service, type) {
            const serviceTotal = this.calculateServiceTotal(service);
            const isSelected = this.state.selectedServices.has(service.id);
            const isExcluded = service.excludeFromMarkup;
            const isFullProfit = service.fullProfit;
            const classes = [];
            if (isSelected) classes.push('selected');
            if (isExcluded) classes.push('excluded');
            if (isFullProfit) classes.push('full-profit');

            // Определяем специфичные поля price/quantity в зависимости от типа
            let priceLabel, quantityLabel, dayField;

            if (type === 'hotel') {
                priceLabel = '💵 ' + _t('app.render.per_night', 'Per night:');
                quantityLabel = '🌙 ' + _t('app.render.nights', 'Nights:');
                dayField = false; // Для отелей нет поля "день"
            } else if (type === 'flight') {
                priceLabel = '💵 ' + _t('app.render.per_ticket', 'Per ticket:');
                quantityLabel = '👥 ' + _t('app.render.passengers', 'Passengers:');
                dayField = false; // Для перелётов нет поля "день"
            } else if (type === 'other') {
                priceLabel = _t('app.render.price', 'Price:');
                quantityLabel = _t('app.render.qty', 'Qty:');
                dayField = false; // Для прочих услуг нет поля "день"
            } else {
                priceLabel = _t('app.render.price', 'Price:');
                quantityLabel = _t('app.render.qty', 'Qty:');
                dayField = true; // Для основных услуг есть поле "день"
            }

            // Регион - для отелей из селекта, для остальных не показываем
            const regionHtml = type === 'hotel' ? `
                <div style="font-size: 11px; color: var(--gray-500); margin-top: 1px; display: flex; align-items: center; gap: 4px;">
                    <i data-lucide="map-pin" style="width: 10px; height: 10px; display: inline; vertical-align: text-top;"></i>
                    <select class="${type}-region-select" data-service-id="${service.id}" style="font-size: 11px; border: none; background: transparent; color: var(--gray-500); padding: 0; cursor: pointer;" title="${_t('app.render.change_region', 'Change region')}">
                        ${this.regions.map(region => `<option value="${region}" ${service.region === region ? 'selected' : ''}>${region}</option>`).join('')}
                    </select>
                </div>
            ` : '';

            return `
                <div class="service-card ${classes.join(' ')}" data-service-id="${service.id}" data-service-type="${type}">
                    <div class="service-header">
                        <div class="service-header-left">
                            <input type="checkbox" class="checkbox ${type}-checkbox" ${isSelected ? 'checked' : ''}
                                   data-service-id="${service.id}"
                                   title="${_t('app.render.select_for_bulk', 'Select for bulk operations')}">
                            <div>
                                <div class="editable" contenteditable="true" data-field="name" data-id="${service.id}" style="font-size: 14px; font-weight: 600; color: var(--gray-900); line-height: 1.3;">${service.name}</div>
                                ${regionHtml}
                            </div>
                        </div>
                        <div style="display: flex; align-items: center; gap: 8px;">
                            <!-- Статусы бронирования и оплаты -->
                            <div style="display: flex; align-items: center; gap: 6px;">
                                <button
                                    class="status-icon-btn ${type}-booking-status-btn"
                                    data-service-id="${service.id}"
                                    data-status="${service.done ? 'booked' : 'none'}"
                                    title="${_t('app.render.booking_status', 'Booking status')}: ${service.done ? _t('app.render.booked', 'Booked') : _t('app.render.not_booked', 'Not booked')}"
                                    style="border: none; background: none; cursor: pointer; padding: 4px; border-radius: 4px; transition: all 0.2s; color: ${service.done ? '#10b981' : '#9ca3af'};">
                                    <i data-lucide="${service.done ? 'check-circle' : 'circle'}" style="width: 18px; height: 18px;"></i>
                                </button>
                                <button
                                    class="status-icon-btn ${type}-payment-status-btn"
                                    data-service-id="${service.id}"
                                    data-status="${service.paid ? 'paid' : 'none'}"
                                    title="${_t('app.render.payment_status', 'Payment status')}: ${service.paid ? _t('app.render.paid', 'Paid') : _t('app.render.not_paid', 'Not paid')}"
                                    style="border: none; background: none; cursor: pointer; padding: 4px; border-radius: 4px; transition: all 0.2s; color: ${service.paid ? '#10b981' : '#9ca3af'};">
                                    <i data-lucide="${service.paid ? 'circle-dollar-sign' : 'dollar-sign'}" style="width: 18px; height: 18px;"></i>
                                </button>
                            </div>
                            <button class="btn btn-sm duplicate-${type}-btn" data-service-id="${service.id}" title="${_t('app.render.duplicate', 'Duplicate')}" style="padding: 4px 8px; font-size: 12px; background: transparent; color: #9ca3af; border: 1px solid #e5e7eb; margin-right: 4px; transition: all 0.2s;">
                                <i data-lucide="copy" style="width: 14px; height: 14px;"></i>
                            </button>
                            <button class="btn btn-sm remove-${type}-btn" data-service-id="${service.id}" title="${_t('btn.delete', 'Delete')}">×</button>
                        </div>
                    </div>
                    <div class="service-content">
                        <div class="form-row" style="align-items: flex-start; margin-bottom: calc(var(--spacing) * 1);">
                            <label style="padding-top: 3px; font-size: 10px; color: var(--gray-500); text-transform: uppercase; letter-spacing: 0.3px; width: 100px; flex-shrink: 0;">${_t('app.render.description_label', 'Description')}:</label>
                            <div class="editable" contenteditable="true" data-field="description" data-id="${service.id}" style="flex: 1; min-width: 0; font-size: 12px; color: var(--gray-700); line-height: 1.5;">${service.description}</div>
                        </div>

                        <div class="form-row" style="align-items: flex-start; margin-bottom: calc(var(--spacing) * 1);">
                            <label style="padding-top: 3px; font-size: 10px; color: var(--gray-500); text-transform: uppercase; letter-spacing: 0.3px; width: 100px; flex-shrink: 0;">${_t('app.render.contractor_label', 'Contractor')}:</label>
                            <div class="editable" contenteditable="true" data-field="contractor" data-id="${service.id}" style="flex: 1; min-width: 0; font-size: 12px; color: var(--gray-700); line-height: 1.5;">${this.getSupplierName(service)}</div>
                        </div>

                        <div class="form-row" style="margin-bottom: calc(var(--spacing) * 1); flex-wrap: wrap; row-gap: calc(var(--spacing) * 0.75);">
                            ${dayField ? `
                                <div style="display: flex; align-items: center; gap: 6px;">
                                    <label style="font-size: 10px; color: var(--gray-500); white-space: nowrap;">${_t('app.render.day_label', 'Day')}:</label>
                                    <input type="number" class="input ${type}-field" data-field="day" data-service-id="${service.id}" value="${service.day}" min="1" style="width: 50px; font-size: 11px; padding: 4px 6px; text-align: center;" autocomplete="off" data-lpignore="true" data-form-type="other">
                                </div>
                            ` : ''}

                            <div style="display: flex; align-items: center; gap: 6px;">
                                <label style="font-size: 10px; color: var(--gray-500); white-space: nowrap;">${priceLabel}</label>
                                <input type="number" class="input ${type}-field" data-field="price" data-service-id="${service.id}" value="${service.price}" min="0" style="width: 80px; font-size: 11px; padding: 4px 6px; text-align: center;" autocomplete="off" data-lpignore="true" data-form-type="other">
                                ${this._currencySelectHtml(service, type)}
                            </div>

                            <div style="display: flex; align-items: center; gap: 6px;">
                                <label style="font-size: 10px; color: var(--gray-500); white-space: nowrap;">${quantityLabel}</label>
                                <div class="qty-control">
                                    <button class="qty-btn qty-decrease" data-service-id="${service.id}" data-type="${type}">−</button>
                                    <input type="number" class="qty-input ${type}-field" data-field="quantity" data-service-id="${service.id}" value="${service.quantity}" min="1" autocomplete="off" data-lpignore="true" data-form-type="other">
                                    <button class="qty-btn qty-increase" data-service-id="${service.id}" data-type="${type}">+</button>
                                </div>
                            </div>

                            ${type === 'hotel' ? `
                                <div style="display: flex; align-items: center; gap: 6px;">
                                    <label style="font-size: 10px; color: var(--gray-500); white-space: nowrap;">🚪 ${_t('app.render.rooms_count', 'Rooms')}:</label>
                                    <input type="number" class="input ${type}-field" data-field="roomsCount" data-service-id="${service.id}" value="${service.roomsCount || 1}" min="1" style="width: 60px; font-size: 11px; padding: 4px 6px; text-align: center;" autocomplete="off" data-lpignore="true" data-form-type="other">
                                </div>
                            ` : ''}

                            <div style="display: flex; align-items: center; gap: 6px;">
                                <label style="font-size: 10px; color: var(--gray-500); white-space: nowrap;">${_t('app.render.markup_label', 'Markup')}:</label>
                                <input type="number" class="input ${type}-field" data-field="markup" data-service-id="${service.id}" value="${service.markup || 0}" min="0" style="width: 50px; font-size: 11px; padding: 4px 6px; text-align: center;" autocomplete="off" data-lpignore="true" data-form-type="other">
                                <span style="font-size: 10px; color: var(--gray-500);">%</span>
                            </div>
                        </div>

                        <!-- Время начала/окончания -->
                        <div style="border-top: 1px solid #f3f4f6; padding-top: calc(var(--spacing) * 0.5);">
                            <button
                                class="btn btn-sm ${type}-timing-toggle-btn"
                                data-service-id="${service.id}"
                                style="font-size: 9px; padding: 2px 6px; background: #f9fafb; color: #6b7280; border: none; width: 100%; text-align: left; display: flex; align-items: center; gap: 4px;"
                                title="${_t('app.render.show_timing', 'Show start/end time')}">
                                <span class="timing-toggle-icon" style="font-size: 8px;">▶</span>
                                <span>⏰ ${_t('app.render.timing', 'Timing')}</span>
                                ${service.startTime || service.endTime ? `<span style="font-size: 8px; color: #3b82f6;">●</span>` : ''}
                            </button>
                            <div class="timing-content" id="${type}-timing-${service.id}" style="max-height: 0; overflow: hidden; transition: max-height 0.3s ease;">
                                <div style="padding: calc(var(--spacing) * 0.75) 0; display: flex; flex-wrap: wrap; gap: calc(var(--spacing) * 0.75);">
                                    <div style="display: flex; align-items: center; gap: 6px;">
                                        <label style="font-size: 10px; color: var(--gray-500); white-space: nowrap;">⏰ ${_t('app.render.start_time', 'Start')}:</label>
                                        <input type="time" class="input ${type}-field" data-field="startTime" data-service-id="${service.id}" value="${service.startTime || ''}" style="width: 90px; font-size: 11px; padding: 4px 6px;">
                                    </div>

                                    <div style="display: flex; align-items: center; gap: 6px;">
                                        <label style="font-size: 10px; color: var(--gray-500); white-space: nowrap;">${_t('app.render.end_time', 'End')}:</label>
                                        <input type="time" class="input ${type}-field" data-field="endTime" data-service-id="${service.id}" value="${service.endTime || ''}" style="width: 90px; font-size: 11px; padding: 4px 6px;">
                                    </div>

                                    <div style="display: flex; align-items: center; gap: 6px;">
                                        <span style="font-size: 10px; color: var(--gray-500);">${_t('app.render.duration', 'Duration')}:</span>
                                        <span style="font-size: 11px; font-weight: 500; color: var(--gray-700);" id="${type}-duration-${service.id}">
                                            ${this.calculateDuration(service.startTime, service.endTime)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="form-row" style="margin-bottom: 0; align-items: center; flex-wrap: wrap; row-gap: calc(var(--spacing) * 1);">
                            <label style="display: flex; align-items: center; gap: 6px; font-size: 10px; color: var(--gray-600); white-space: nowrap;">
                                <input type="checkbox" class="checkbox ${type}-exclude-checkbox" data-service-id="${service.id}" ${service.excludeFromMarkup ? 'checked' : ''} title="${_t('app.render.exclude_markup_hint', 'Disable markup for this service')}">
                                <span>${_t('app.render.exclude_markup', 'Disable markup')}</span>
                            </label>

                            <label style="display: flex; align-items: center; gap: 6px; font-size: 10px; color: var(--gray-600); white-space: nowrap;">
                                <input type="checkbox" class="checkbox ${type}-full-profit-checkbox" data-service-id="${service.id}" ${service.fullProfit ? 'checked' : ''} title="${_t('app.render.full_profit_hint', 'Whole amount as profit (cost = 0)')}">
                                <span>💰 ${_t('app.render.full_profit', 'Full amount as profit')}</span>
                            </label>

                            <div style="display: flex; align-items: center; gap: 6px;">
                                <label style="font-size: 10px; color: var(--gray-600); white-space: nowrap;">💳 ${_t('app.render.prepayment_label', 'Prepayment')}:</label>
                                <input type="number" class="input ${type}-field" data-field="prepayment" data-service-id="${service.id}" value="${service.prepayment || 0}" min="0" step="0.01" style="width: 90px; font-size: 11px; padding: 4px 6px;" title="${_t('app.render.prepayment_hint', 'Required prepayment amount')}">
                            </div>

                            <div style="display: flex; align-items: center; gap: 6px;">
                                <label style="font-size: 10px; color: var(--gray-600); white-space: nowrap;">📅 ${_t('app.render.prepayment_deadline_label', 'Prepayment due')}:</label>
                                <input type="date" class="input ${type}-field" data-field="prepaymentDeadline" data-service-id="${service.id}" value="${service.prepaymentDeadline || ''}" style="width: 130px; font-size: 11px; padding: 4px 6px;" title="${_t('app.render.prepayment_deadline_hint', 'Latest date for prepayment')}">
                            </div>

                            <div style="margin-left: auto; text-align: right; min-width: 80px;">
                                <div style="font-size: 15px; font-weight: 600; color: var(--success); white-space: nowrap;">
                                    ${this.formatCurrency(serviceTotal)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            `;
        },

        showAddHotelModal() {
            // Открываем модальное окно создания отеля
            this.state.currentServiceType = 'hotel';
            this.showServiceModal();
        },

        showAddFlightModal() {
            // Открываем модальное окно создания перелёта
            this.state.currentServiceType = 'flight';
            this.showServiceModal();
        },

        showAddOtherServiceModal() {
            // Открываем модальное окно создания прочей услуги
            this.state.currentServiceType = 'other';
            this.showServiceModal();
        },
    });
}
